import { ref } from "vue";
import { defineStore } from "pinia";
import { $http } from "@/plugins/api";
import { getApiClientUrl } from "@/config/runtimeConfig";
import { DEFAULT_BRANDING_NAME } from "@/modules/Settings/constants";
import type {
  OrganisationBranding,
  OrganisationBrandingForm,
} from "@/types/organisatio";

const defaultBranding = (): OrganisationBranding => ({
  organisationID: "",
  logo: null,
  logo_dark: null,
  name: DEFAULT_BRANDING_NAME,
  inherited: null,
  email_from: null,
});

export const useBrandingStore = defineStore("branding", () => {
  const organisationBranding = ref<OrganisationBranding>(defaultBranding());
  const uploadedLogos = ref<string[]>([]);

  const removeFromUploadedLogos = (logo: string): void => {
    // Extract the filename part from the full path
    const filename = logo.split("/").pop(); // e.g. dark_icons8-chat-bubble-100.png
    if (!filename) return;

    // Get the prefix ("dark_" or "light_")
    const prefixMatch = filename.match(/^(dark_|light_)/);
    if (!prefixMatch) return;

    const prefix = prefixMatch[1]; // "dark_" or "light_"

    // Removal of all entries that have filename starting with the same prefix
    for (let i = uploadedLogos.value.length - 1; i >= 0; i--) {
      const file = uploadedLogos.value[i].split("/").pop();
      if (file?.startsWith(prefix)) {
        uploadedLogos.value.splice(i, 1);
      }
    }
  };

  const uploadLogo = async ({
    logo,
    logo_dark,
    organisationUuid,
  }: {
    logo?: File;
    logo_dark?: File;
    organisationUuid: string | undefined;
  }) => {
    if (!organisationUuid) {
      throw new Error("Organisation ID is not provided");
    }

    const formData = new FormData();

    if (logo) {
      formData.append("logo", logo);
    }
    if (logo_dark) {
      formData.append("logo_dark", logo_dark);
    }

    formData.append("organisationID", organisationUuid);

    const response = await $http.post(
      `${getApiClientUrl()}/v1/pt/org/${organisationUuid}/branding/logo/upload`,
      formData,
      {
        headers: {
          "Content-Type": "multipart/form-data",
        },
      },
    );

    const uploadedLogo = response.data.logo || response.data.logo_dark;
    if (uploadedLogo) {
      uploadedLogos.value.push(uploadedLogo);
    }

    return response.data;
  };

  const removeLogo = async ({
    organisationUuid,
    logo,
  }: {
    organisationUuid: string | undefined;
    logo: string;
  }) => {
    if (!organisationUuid) {
      throw new Error("Organisation ID is not provided");
    }

    removeFromUploadedLogos(logo);

    return await $http.delete(
      `${getApiClientUrl()}/v1/pt/org/${organisationUuid}/branding/logo/upload/${logo}?force=1`,
    );
  };

  const getBranding = async ({
    organisationUuid,
  }: {
    organisationUuid: string | undefined;
  }) => {
    if (!organisationUuid) {
      throw new Error("Organisation ID is not provided");
    }
    uploadedLogos.value = [];

    const response = await $http.get(
      `${getApiClientUrl()}/v1/pt/org/${organisationUuid}/branding`,
    );

    organisationBranding.value = response.data;

    return response.data;
  };

  const getUploadedLogos = async ({
    organisationUuid,
  }: {
    organisationUuid: string | undefined;
  }) => {
    if (!organisationUuid) {
      throw new Error("Organisation ID is not provided");
    }

    const response = await $http.get(
      `${getApiClientUrl()}/v1/pt/org/${organisationUuid}/branding/logo/upload`,
    );

    uploadedLogos.value = response.data.logos;

    return response.data.logos;
  };

  const restoreName = async ({
    organisationUuid,
  }: {
    organisationUuid: string | undefined;
  }) => {
    if (!organisationUuid) {
      throw new Error("Organisation ID is not provided");
    }

    const response = await $http.post(
      `${getApiClientUrl()}/v1/pt/org/${organisationUuid}/branding/name/reset`,
    );
    organisationBranding.value.name = DEFAULT_BRANDING_NAME;
    return response.data;
  };

  const restoreLogo = async ({
    organisationUuid,
  }: {
    organisationUuid: string | undefined;
  }) => {
    if (!organisationUuid) {
      throw new Error("Organisation ID is not provided");
    }

    const response = await $http.post(
      `${getApiClientUrl()}/v1/pt/org/${organisationUuid}/branding/logo/reset`,
    );
    organisationBranding.value.logo = null;
    organisationBranding.value.logo_dark = null;
    organisationBranding.value.inherited = null;
    uploadedLogos.value = [];
    return response.data;
  };

  const saveBrandingName = async ({
    organisationUuid,
    name,
    email_from,
  }: {
    organisationUuid: string | undefined;
    name: string;
    email_from?: string | null;
  }) => {
    if (!organisationUuid) {
      throw new Error("Organisation ID is not provided");
    }

    const payload: Record<string, unknown> = {};

    if (name) {
      payload.name = name;
    }

    if (email_from !== undefined) {
      payload.email_from = email_from;
    }

    const response = await $http.put(
      `${getApiClientUrl()}/v1/pt/org/${organisationUuid}/branding/name`,
      payload,
    );

    return response.data;
  };

  const saveBrandingLogo = async ({
    organisationUuid,
    form,
  }: {
    organisationUuid: string | undefined;
    form: Partial<OrganisationBrandingForm>;
  }) => {
    if (!organisationUuid) {
      throw new Error("Organisation ID is not provided");
    }

    const payload: Record<string, unknown> = {};

    if (form.light_logo) {
      payload.logo = form.light_logo.startsWith("/")
        ? form.light_logo.slice(1)
        : form.light_logo;
    }
    if (form.dark_logo) {
      payload.logo_dark = form.dark_logo.startsWith("/")
        ? form.dark_logo.slice(1)
        : form.dark_logo;
    }

    const response = await $http.put(
      `${getApiClientUrl()}/v1/pt/org/${organisationUuid}/branding/logo`,
      payload,
    );

    return response.data;
  };

  const removeUploadedLogos = async ({
    organisationUuid,
  }: {
    organisationUuid: string | undefined;
  }) => {
    if (!organisationUuid) {
      throw new Error("Organisation ID is not provided");
    }

    const response = await $http.delete(
      `${getApiClientUrl()}/v1/pt/org/${organisationUuid}/branding/logo/upload`,
    );
    uploadedLogos.value = [];
    return response.data;
  };

  return {
    organisationBranding,
    uploadedLogos,
    uploadLogo,
    removeLogo,
    getBranding,
    getUploadedLogos,
    restoreName,
    restoreLogo,
    saveBrandingName,
    saveBrandingLogo,
    removeUploadedLogos,
  };
});
